/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { ReduxUtils } from '@spm/core';

import { FETCH_SURVEY_QUESTIONS, POST_SURVEY_ANSWERS } from './actionTypes';

import { fetchSurveyQuestionsUtil, postSurveyAnswersUtil } from './utils';

/**
 * Contains the action creator functions that update the Survey slice of the store.
 *
 * @namespace SurveyActions
 */
export default class actions {
  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/survey_questions
   *
   * @param {string} language.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof SurveyActions
   */
  static fetchSurveyQuestions = (dispatch, language, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      fetchSurveyQuestionsUtil.bind(null, language),

      FETCH_SURVEY_QUESTIONS,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to fetchSurveyQuestions.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof SurveyActions
   */
  static resetFetchSurveyQuestions = (dispatch, data) => {
    const json = {
      type: FETCH_SURVEY_QUESTIONS.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to fetchSurveyQuestions.
   *
   * @param {function} dispatch
   * @function
   * @memberof SurveyActions
   */
  static resetFetchSurveyQuestionsError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, FETCH_SURVEY_QUESTIONS.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/survey_answers
   *
   * @param {NewSDPostSurveyAnswersStruct} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof SurveyActions
   */
  static postSurveyAnswers = (dispatch, newSDPostSurveyAnswersStruct, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postSurveyAnswersUtil.bind(null, newSDPostSurveyAnswersStruct),
      POST_SURVEY_ANSWERS,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postSurveyAnswers.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof SurveyActions
   */
  static resetPostSurveyAnswers = (dispatch, data) => {
    const json = {
      type: POST_SURVEY_ANSWERS.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postSurveyAnswers.
   *
   * @param {function} dispatch
   * @function
   * @memberof SurveyActions
   */
  static resetPostSurveyAnswersError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_SURVEY_ANSWERS.failureAction);
  };
}
